/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    SessionSubscriptionsResponse,
    UserDataStreamSubscribeResponse,
    UserDataStreamSubscribeSignatureResponse,
    UserDataStreamUnsubscribeResponse,
} from '../types';

/**
 * UserDataStreamApi - interface
 *
 * @interface UserDataStreamApi
 */
export interface UserDataStreamApiInterface {
    /**
     * **Note:**
     *
     * Users should track the corresponding subscription status of related accounts as needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Listing all subscriptions
     * @param {SessionSubscriptionsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SessionSubscriptionsResponse>}
     * @memberof UserDataStreamApiInterface
     */
    sessionSubscriptions(
        requestParameters?: SessionSubscriptionsRequest
    ): Promise<WebsocketApiResponse<SessionSubscriptionsResponse>>;

    /**
     * Subscribe to the User Data Stream in the current WebSocket connection.
     *
     **Notes:**
     * - This method requires an authenticated WebSocket connection using Ed25519 keys. Please refer to [`session.logon`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-logon).
     * - To check the subscription status, use [`session.status`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-status), see the `userDataStream` flag indicating you have have an active subscription.
     * - User Data Stream events are available in both JSON and [SBE](/products/spot/faqs/sbe_faq) sessions.
     * - Please refer to [User Data Streams](/products/spot/user-data-stream) for the event format details.
     * - For SBE, only SBE schema 2:1 or later is supported.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * @summary Subscribe to User Data Stream
     * @param {UserDataStreamSubscribeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<UserDataStreamSubscribeResponse>}
     * @memberof UserDataStreamApiInterface
     */
    userDataStreamSubscribe(
        requestParameters?: UserDataStreamSubscribeRequest
    ): Promise<WebsocketApiResponse<UserDataStreamSubscribeResponse>>;

    /**
     * Weight(IP): 2
     *
     * Security Type: USER_STREAM
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Subscribe to User Data Stream through signature subscription (USER_STREAM)
     * @param {UserDataStreamSubscribeSignatureRequest} requestParameters Request parameters.
     *
     * @returns {Promise<UserDataStreamSubscribeSignatureResponse>}
     * @memberof UserDataStreamApiInterface
     */
    userDataStreamSubscribeSignature(
        requestParameters?: UserDataStreamSubscribeSignatureRequest
    ): Promise<WebsocketApiResponse<UserDataStreamSubscribeSignatureResponse>>;

    /**
     * Stop listening to the User Data Stream in the current WebSocket
     * connection.
     *
     * Note that `session.logout` will only close the subscription created with `userDataStream.subscribe` but not subscriptions opened with `userDataStream.subscribe.signature`.
     *
     * Weight(IP): 2
     *
     * @summary WebSocket Unsubscribe from User Data Stream
     * @param {UserDataStreamUnsubscribeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<UserDataStreamUnsubscribeResponse>}
     * @memberof UserDataStreamApiInterface
     */
    userDataStreamUnsubscribe(
        requestParameters?: UserDataStreamUnsubscribeRequest
    ): Promise<WebsocketApiResponse<UserDataStreamUnsubscribeResponse>>;
}

/**
 * Request parameters for sessionSubscriptions operation in UserDataStreamApi.
 * @interface SessionSubscriptionsRequest
 */
export interface SessionSubscriptionsRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof UserDataStreamApiSessionSubscriptions
     */
    readonly id?: string;
}

/**
 * Request parameters for userDataStreamSubscribe operation in UserDataStreamApi.
 * @interface UserDataStreamSubscribeRequest
 */
export interface UserDataStreamSubscribeRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof UserDataStreamApiUserDataStreamSubscribe
     */
    readonly id?: string;
}

/**
 * Request parameters for userDataStreamSubscribeSignature operation in UserDataStreamApi.
 * @interface UserDataStreamSubscribeSignatureRequest
 */
export interface UserDataStreamSubscribeSignatureRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof UserDataStreamApiUserDataStreamSubscribeSignature
     */
    readonly id?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof UserDataStreamApiUserDataStreamSubscribeSignature
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for userDataStreamUnsubscribe operation in UserDataStreamApi.
 * @interface UserDataStreamUnsubscribeRequest
 */
export interface UserDataStreamUnsubscribeRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof UserDataStreamApiUserDataStreamUnsubscribe
     */
    readonly id?: string;

    /**
     * When called with no parameter, this will close all subscriptions. When called with the subscriptionId parameter, this will attempt to close the subscription with that subscription id, if it exists.
     * @type {number}
     * @memberof UserDataStreamApiUserDataStreamUnsubscribe
     */
    readonly subscriptionId?: number;
}

/**
 * UserDataStreamApi - object-oriented interface
 * @class UserDataStreamApi
 * @extends {WebsocketAPIBase}
 */
export class UserDataStreamApi implements UserDataStreamApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * **Note:**
     *
     * Users should track the corresponding subscription status of related accounts as needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Listing all subscriptions
     * @param {SessionSubscriptionsRequest} requestParameters Request parameters.
     * @returns {Promise<SessionSubscriptionsResponse>}
     * @memberof UserDataStreamApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#session-subscriptions Binance API Documentation}
     */
    public sessionSubscriptions(
        requestParameters: SessionSubscriptionsRequest = {}
    ): Promise<WebsocketApiResponse<SessionSubscriptionsResponse>> {
        return this.websocketBase.sendMessage<SessionSubscriptionsResponse>(
            '/session.subscriptions'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }

    /**
     * Subscribe to the User Data Stream in the current WebSocket connection.
     *
     **Notes:**
     * - This method requires an authenticated WebSocket connection using Ed25519 keys. Please refer to [`session.logon`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-logon).
     * - To check the subscription status, use [`session.status`](/catalog/core-trading-spot-trading/api/ws-api/auth#session-status), see the `userDataStream` flag indicating you have have an active subscription.
     * - User Data Stream events are available in both JSON and [SBE](/products/spot/faqs/sbe_faq) sessions.
     * - Please refer to [User Data Streams](/products/spot/user-data-stream) for the event format details.
     * - For SBE, only SBE schema 2:1 or later is supported.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * @summary Subscribe to User Data Stream
     * @param {UserDataStreamSubscribeRequest} requestParameters Request parameters.
     * @returns {Promise<UserDataStreamSubscribeResponse>}
     * @memberof UserDataStreamApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-subscribe Binance API Documentation}
     */
    public userDataStreamSubscribe(
        requestParameters: UserDataStreamSubscribeRequest = {}
    ): Promise<WebsocketApiResponse<UserDataStreamSubscribeResponse>> {
        return this.websocketBase.sendMessage<UserDataStreamSubscribeResponse>(
            '/userDataStream.subscribe'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }

    /**
     * Weight(IP): 2
     *
     * Security Type: USER_STREAM
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Subscribe to User Data Stream through signature subscription (USER_STREAM)
     * @param {UserDataStreamSubscribeSignatureRequest} requestParameters Request parameters.
     * @returns {Promise<UserDataStreamSubscribeSignatureResponse>}
     * @memberof UserDataStreamApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-subscribe-signature Binance API Documentation}
     */
    public userDataStreamSubscribeSignature(
        requestParameters: UserDataStreamSubscribeSignatureRequest = {}
    ): Promise<WebsocketApiResponse<UserDataStreamSubscribeSignatureResponse>> {
        return this.websocketBase.sendMessage<UserDataStreamSubscribeSignatureResponse>(
            '/userDataStream.subscribe.signature'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Stop listening to the User Data Stream in the current WebSocket
     * connection.
     *
     * Note that `session.logout` will only close the subscription created with `userDataStream.subscribe` but not subscriptions opened with `userDataStream.subscribe.signature`.
     *
     * Weight(IP): 2
     *
     * @summary WebSocket Unsubscribe from User Data Stream
     * @param {UserDataStreamUnsubscribeRequest} requestParameters Request parameters.
     * @returns {Promise<UserDataStreamUnsubscribeResponse>}
     * @memberof UserDataStreamApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/user-data-stream#user-data-stream-unsubscribe Binance API Documentation}
     */
    public userDataStreamUnsubscribe(
        requestParameters: UserDataStreamUnsubscribeRequest = {}
    ): Promise<WebsocketApiResponse<UserDataStreamUnsubscribeResponse>> {
        return this.websocketBase.sendMessage<UserDataStreamUnsubscribeResponse>(
            '/userDataStream.unsubscribe'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }
}
